import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Lightning, LockKey, ShieldCheck } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const schema = z
  .object({
    password: z.string().min(6, 'Use at least 6 characters'),
    confirm: z.string().min(1, 'Confirm your password'),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

export default function UpdatePasswordPage() {
  const navigate = useNavigate();
  const { updatePassword, checkAuth } = useAuth();
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session) {
        toast.error('Reset link invalid or expired. Request a new one from Sign in.');
        navigate('/login', { replace: true });
        return;
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await updatePassword(values.password);
      await checkAuth();
      toast.success('Password updated');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Could not update password');
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen gradient-aurora flex items-center justify-center">
        <div className="h-12 w-12 rounded-2xl gradient-primary flex items-center justify-center shadow-glow">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-aurora flex flex-col">
      <header className="border-b border-border bg-background/70 backdrop-blur-xl">
        <div className="max-w-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Sign in
          </Link>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg gradient-primary">
              <Lightning size={14} weight="fill" className="text-white" />
            </span>
            <span className="font-display text-lg font-bold text-foreground">LinkFlow</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md surface-card shadow-glow p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="grid place-items-center h-12 w-12 rounded-2xl bg-primary/10 text-primary shrink-0">
              <ShieldCheck size={22} weight="fill" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Account security
              </p>
              <h1 className="font-display text-2xl font-extrabold text-foreground mt-1">
                Set a new password
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Choose a strong password to keep your workspace secure.
              </p>
            </div>
          </div>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <PasswordField
              id="new-password"
              label="New password"
              register={form.register('password')}
              error={form.formState.errors.password?.message}
              autoComplete="new-password"
            />
            <PasswordField
              id="confirm-password"
              label="Confirm password"
              register={form.register('confirm')}
              error={form.formState.errors.confirm?.message}
              autoComplete="new-password"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl gradient-primary text-white py-3 text-sm font-semibold shadow-glow hover:opacity-95 transition-all disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Update password'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}

function PasswordField({ id, label, register, error, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold text-muted-foreground uppercase tracking-[0.14em] mb-1.5"
      >
        {label}
      </label>
      <div className="relative">
        <LockKey
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <input
          id={id}
          type="password"
          autoComplete={autoComplete}
          className={`input-modern pl-10 ${error ? 'border-destructive/60 focus:ring-destructive/20' : ''}`}
          {...register}
        />
      </div>
      {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
    </div>
  );
}
